import {
  AdditionalSwLogInfo,
  LogEventGrouping,
  LogLevel,
} from 'shared/types/logger';
import { HttpError, isNetworkFailure, isNoProjectAccessError } from './httpError';
import { swLog } from './makeSwLogger';
import { getPullDiagnostics } from './pullDiagnostics';

export enum EntityOperation {
  FETCHING = 'fetching',
  SYNCING = 'syncing',
}

interface EntityFailure {
  entityName: string;
  /** Absent for entities that do not belong to a project (e.g. permissions). */
  projectId?: string;
  /** The failure, when there was one; its status is logged as `httpStatus`. */
  error?: unknown;
  /** State of the failed run, logged next to the pull diagnostics. */
  setup?: Record<string, unknown>;
}

function getHttpStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }
  const { status } = error as { status?: unknown };
  return typeof status === 'number' ? status : undefined;
}

/**
 * What a failed entity download needs in its log to be diagnosed: which
 * entity of which project, the HTTP status, the network and the worker state.
 */
export function getEntityFailureLogInfo({
  entityName,
  projectId,
  error,
  setup,
}: EntityFailure): AdditionalSwLogInfo {
  const diagnostics = getPullDiagnostics();
  return {
    entityName,
    projectId,
    httpStatus: getHttpStatus(error),
    online: diagnostics.online,
    setup: { ...diagnostics, ...setup },
  };
}

/**
 * A lost network (offline, laptop asleep) and a project the user has no access
 * to are expected and only logged; a failure the server answered otherwise or a
 * bug is an error.
 */
export function getEntityFailureLevel(error: unknown): LogLevel {
  return isNetworkFailure(error) || isNoProjectAccessError(error) ? LogLevel.INFO : LogLevel.ERROR;
}

/**
 * HTTP errors share one message ("Something went wrong") and differ only by
 * the caller's stack: grouped by operation and status, the entity is a tag.
 */
function getEntityFailureGrouping(
  operation: EntityOperation,
  error: unknown
): LogEventGrouping | undefined {
  return error instanceof HttpError
    ? { fingerprint: ['entity-operation-failed', operation, String(error.status)] }
    : undefined;
}

/** Logs that fetching or syncing an entity failed, with its diagnostics. */
export function logEntityOperationFailure(
  operation: EntityOperation,
  failure: EntityFailure,
  level: LogLevel = getEntityFailureLevel(failure.error)
): void {
  swLog(
    `Problem occurred when ${operation} ${failure.entityName}`,
    level,
    failure.error,
    getEntityFailureLogInfo(failure),
    getEntityFailureGrouping(operation, failure.error)
  );
}

/**
 * Logs a failure whose message does not name the entity, grouped into one
 * issue for all entities; the entity is a tag.
 */
export function logGroupedEntityFailure(
  message: string,
  failure: EntityFailure,
  grouping: LogEventGrouping,
  level: LogLevel = getEntityFailureLevel(failure.error)
): void {
  swLog(message, level, null, getEntityFailureLogInfo(failure), grouping);
}
